// Tryb — MSG91 OTP widget, custom-UI mode.
//
// MSG91 generates, delivers and verifies the code; we keep our own sign-in
// screen. `exposeMethods: true` is what makes that possible — it suppresses
// MSG91's UI and hands us sendOtp / verifyOtp / retryOtp instead.
//
// Verification finishes server-side: the widget returns an access token, and
// only the API can turn that into a trusted phone number, because only the API
// holds the authkey.

const SCRIPT_URL = "https://verify.msg91.com/otp-provider.js";

// How long to let MSG91 fetch its captcha token after the SDK reports ready.
const CAPTCHA_GRACE_MS = 1200;

export const msg91Config = {
  widgetId: import.meta.env.VITE_MSG91_WIDGET_ID || "",
  tokenAuth: import.meta.env.VITE_MSG91_TOKEN_AUTH || "",
  // Must match the OTP length set on the widget in MSG91's dashboard.
  otpLength: Number(import.meta.env.VITE_MSG91_OTP_LENGTH) || 4,
};

export const msg91Enabled = () => !!(msg91Config.widgetId && msg91Config.tokenAuth);

let loading = null;

function loadScript() {
  if (window.initSendOTP) return Promise.resolve();
  if (loading) return loading;

  loading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_URL;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      loading = null;
      reject(new Error("Couldn’t load the verification service"));
    };
    document.head.appendChild(s);
  });
  return loading;
}

let ready = null;

/** Load and initialise the widget once per page load. */
export function initMsg91() {
  if (!msg91Enabled()) return Promise.reject(new Error("MSG91 isn’t configured"));
  if (ready) return ready;

  ready = loadScript().then(
    () =>
      new Promise((resolve, reject) => {
        try {
          window.initSendOTP({
            widgetId: msg91Config.widgetId,
            tokenAuth: msg91Config.tokenAuth,
            exposeMethods: true,
            success: () => {},
            failure: () => {},
          });
        } catch (e) {
          ready = null;
          return reject(e);
        }

        // initSendOTP attaches the methods almost immediately, but it also
        // fetches a captcha token in the background and sending before that
        // lands fails with "Invalid Captcha Token". There is nothing exposed to
        // wait on, so settle for the methods appearing plus a grace period —
        // and initMsg91 is called on mount, so this is over long before anyone
        // has finished typing a phone number.
        const started = Date.now();
        const poll = () => {
          if (window.sendOtp && window.verifyOtp) return setTimeout(resolve, CAPTCHA_GRACE_MS);
          if (Date.now() - started > 8000) {
            ready = null;
            return reject(new Error("The verification service didn’t start"));
          }
          setTimeout(poll, 50);
        };
        poll();
      }),
  );

  return ready;
}

/** MSG91's callbacks hand back either a string or {message}. Normalise both. */
function text(v) {
  if (typeof v === "string") return v;
  return v?.message || v?.type || JSON.stringify(v);
}

const callbackToPromise =
  (method) =>
  (...args) =>
    new Promise((resolve, reject) => {
      const fn = window[method];
      if (!fn) return reject(new Error("The verification service isn’t ready"));
      fn(...args, (data) => resolve(text(data)), (err) => reject(new Error(text(err))));
    });

const isCaptchaError = (e) => /captcha/i.test(e?.message || "");

/**
 * Send a code. `identifier` is the phone in E.164 without the plus.
 *
 * Retries once on a captcha failure: the token is fetched in the background and
 * can still be in flight if someone signs in the instant the page loads.
 */
export async function sendOtp(identifier) {
  await initMsg91();
  try {
    return await callbackToPromise("sendOtp")(identifier);
  } catch (e) {
    if (!isCaptchaError(e)) throw e;
    await new Promise((r) => setTimeout(r, 1500));
    return callbackToPromise("sendOtp")(identifier);
  }
}

/** Check a code. Resolves to MSG91's access token, which the API then verifies. */
export async function verifyOtp(code) {
  await initMsg91();
  return callbackToPromise("verifyOtp")(code);
}

/**
 * Ask for another code. `channel` is MSG91's numeric channel id —
 * 11 SMS, 12 email, 4 WhatsApp, 3 voice. Omit for the widget's own default.
 */
export async function retryOtp(channel) {
  await initMsg91();
  return callbackToPromise("retryOtp")(channel ?? null);
}
